import {useRef, useMemo, useCallback} from 'react';
import {generatePath} from 'react-router-dom';
// @ts-expect-error
import type {ContextRouter} from 'react-router-dom';

import {IconOnlyButton} from 'shells/button/icon-only-button/icon-only-button';
import {SearchRegularIcon} from 'shells/icon/svg/search-regular-icon';
import {Stack} from 'shells/layout/stack/stack';
import {SelectPickerGeneric} from 'shells/select-picker-generic/select-picker-generic';
import {Body} from 'shells/typography/body';

import {useGetInboxThreadCounts} from '../graphql/hooks/use-get-inbox-thread-counts';

import {getStatusCount, getThreadStatusOptions} from './helpers';

const UNASSIGNED_VIEW = 'unassigned';
const CHATBOT_VIEW = 'chatbot';

const RESTRICTED_VIEWS = [UNASSIGNED_VIEW, CHATBOT_VIEW];

type Props = ContextRouter & {
    selectedView: string | null | undefined;
    threadCount: number;
    assigneeIds: string[];
    isLoadingLatestThreads: boolean;
    onSearchToggle?: () => void;
    isSearchActive?: boolean;
};

export function ThreadListHeader(props: Props) {
    const currentSection = useRef(props.match.params.section || 'all').current;
    const {inboxThreadCounts, isLoading} = useGetInboxThreadCounts({
        assignees: props.assigneeIds,
    });

    const threadStatusOptions = useMemo(
        () => (inboxThreadCounts ? getThreadStatusOptions(currentSection, inboxThreadCounts) : []),
        [currentSection, inboxThreadCounts]
    );

    const dropdownOptions = useMemo(() => {
        return threadStatusOptions.map((option) => {
            const count = isLoading
                ? '…'
                : getStatusCount(currentSection, inboxThreadCounts, option.value.toLowerCase());

            const countLabel = count !== null ? ` (${count})` : '';

            return {
                label: `${option.label}${countLabel}`,
                value: option.value.toLowerCase(),
            };
        });
    }, [threadStatusOptions, isLoading, currentSection, inboxThreadCounts]);

    const selectedOption = useMemo(() => {
        const selectedValue = props.selectedView || 'open';
        return (
            dropdownOptions.find((option) => option.value === selectedValue) || dropdownOptions[0]
        );
    }, [dropdownOptions, props.selectedView]);

    const handleChange = useCallback(
        (option: {label: string; value: string}) => {
            const newPath = generatePath(props.match.path, {
                section: props.match.params.section,
                page: option.value,
            });

            props.history.push({
                pathname: newPath,
                search: props.history.location.search,
            });
        },
        [props.match.path, props.match.params.section, props.history]
    );

    const isRestrictedView = RESTRICTED_VIEWS.includes(props.selectedView);

    if (isRestrictedView) {
        return (
            <div className='flex align-center justify-sb full-height full-width px-8 border-box'>
                <Stack spacing={16} isHorizontal={true} alignItems='center'>
                    <Body color='dark' isBold={true}>
                        {props.selectedView === UNASSIGNED_VIEW ? 'Unassigned' : 'Chatbot'}
                    </Body>
                    <Body color='light' size='small'>
                        {props.isLoadingLatestThreads ? '…' : `${props.threadCount} total`}
                    </Body>
                </Stack>
                {props.onSearchToggle && (
                    <IconOnlyButton
                        onClick={props.onSearchToggle}
                        icon={SearchRegularIcon}
                        isActive={props.isSearchActive}
                    />
                )}
            </div>
        );
    }

    return (
        <div className='flex align-center gap-8 px-8 full-width border-box'>
            <div className='flex-grow min-width-0'>
                <SelectPickerGeneric
                    name='thread-list-status'
                    isSearchable={false}
                    isDark={false}
                    options={dropdownOptions}
                    value={selectedOption}
                    onChange={handleChange}
                    styles={{
                        // @ts-expect-error
                        menu: (base) => ({
                            ...base,
                            zIndex: 11,
                        }),
                    }}
                />
            </div>
            {props.onSearchToggle && (
                <IconOnlyButton
                    onClick={props.onSearchToggle}
                    icon={SearchRegularIcon}
                    isActive={props.isSearchActive}
                />
            )}
        </div>
    );
}
